/**
 * Utilitários de Segurança
 * Funções para validação e sanitização de inputs do usuário
 */

import i18n from '../config/i18n';

export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
  
  return sanitized.trim();
}

export function containsSQLInjection(input: string): boolean {
  if (!input) return false;
  
  const sqlPatterns = [
    /(\s|^)(DROP|SELECT|INSERT|UPDATE|DELETE|UNION|ALTER|CREATE|EXEC|EXECUTE)(\s|$)/gi,
    /(--|\/\*|\*\/|xp_|sp_)/gi,
    /;\s*(DROP|SELECT|INSERT|UPDATE|DELETE)/gi,
    /(OR|AND)\s+\d+\s*=\s*\d+/gi,
    /(\bOR\b|\bAND\b)\s+['"]?\w+['"]?\s*=\s*['"]?\w+['"]?/gi,
    /['"]{2,}/gi,
  ];
  
  return sqlPatterns.some(pattern => pattern.test(input));
}

export function isValidEmail(email: string): boolean {
  if (!email || email.length > 255) return false;
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function isValidName(name: string): boolean {
  if (!name || name.length < 2 || name.length > 255) return false;
  
  const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/;
  return nameRegex.test(name);
}

export function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  if (!password || password.length < 8) return 'weak';
  
  let strength = 0;
  
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  
  if (strength <= 2) return 'weak';
  if (strength <= 4) return 'medium';
  return 'strong';
}

export function isWithinSizeLimit(input: string, maxLength: number): boolean {
  return input.length <= maxLength;
}

export function validateSecureInput(
  input: string,
  fieldName: string,
  options: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    validateName?: boolean;
    validateEmail?: boolean;
  } = {}
): string | null {
  const {
    required = false,
    minLength = 0,
    maxLength = 255,
    validateName = false,
    validateEmail = false,
  } = options;
  
  if (required && (!input || input.trim().length === 0)) {
    return i18n.t('validation.required', { field: fieldName });
  }
  
  if (!input || input.trim().length === 0) {
    return null;
  }
  
  if (input.length < minLength) {
    return i18n.t('validation.minLength', { field: fieldName, min: minLength });
  }
  
  if (input.length > maxLength) {
    return i18n.t('validation.maxLength', { field: fieldName, max: maxLength });
  }
  
  if (validateName && !isValidName(input)) {
    return i18n.t('validation.nameFormat', { field: fieldName });
  }
  
  if (validateEmail && !isValidEmail(input)) {
    return i18n.t('validation.emailFormat', { field: fieldName });
  }
  
  if (!validateName && !validateEmail && containsSQLInjection(input)) {
    return i18n.t('validation.invalidCharacters', { field: fieldName });
  }
  
  return null;
}
